import type { CatchballLevelConfig, CatchballLevelParticipant } from '@/types/prioritization/catchballLoop';
import type { DesignerPath, OrgPathNode, TrackParams } from '../types';
import type { PathCadence } from './cadenceSchedule';
import { buildOrgById, pathChildrenOf } from './pathBuilder';

function tierForRelativeLevel(relativeLevel: number, maxRelativeLevel: number): 'executive' | 'middle' | 'bottom' {
  if (relativeLevel <= 0) return 'executive';
  // Depth-1 paths (anchor → one stop): that stop is the first toss target.
  // Mapping it to "bottom" left `middle` empty, so toss_to_middle could not
  // auto-assign the named holder and client notify/tasks resolved nobody —
  // especially common for CUSTOM paths with a single named recipient.
  if (maxRelativeLevel === 1) return 'middle';
  if (relativeLevel >= maxRelativeLevel) return 'bottom';
  return 'middle';
}

export function pathToLevelConfig(
  path: DesignerPath,
  orgNodes: OrgPathNode[],
  params: TrackParams,
  /** When provided, the computed cadence rides into the level config. */
  cadence?: PathCadence | null,
): CatchballLevelConfig {
  const orgById = buildOrgById(orgNodes);
  const maxRelativeLevel = orgNodes.reduce((max, n) => Math.max(max, n.relativeLevel), 0);

  const executive: CatchballLevelConfig['executive'] = [];
  const middle: CatchballLevelConfig['middle'] = [];
  const bottom: CatchballLevelConfig['bottom'] = [];

  for (const nodeId of path.nodes) {
    const node = orgById.get(nodeId);
    if (!node) continue;
    const tier = tierForRelativeLevel(node.relativeLevel, maxRelativeLevel);
    const assignment = path.nodeAssignments?.[nodeId];
    // A specific user/group pick must resolve to ONLY that pick — orgUnitId is
    // still recorded for traceability, but getRecipientsForPossession only
    // broadens to "everyone at this node" when neither userId nor groupId is
    // set (owner ruling 2026-07-09: unassigned nodes stay open to anyone valid
    // there; assigned nodes go to exactly the assignee).
    const participant: CatchballLevelParticipant =
      assignment?.type === 'user'
        ? { userId: assignment.id, role: 'lead', orgUnitId: nodeId }
        : assignment?.type === 'group'
          ? { userId: '', role: 'lead', orgUnitId: nodeId, groupId: assignment.id }
          : { userId: '', role: 'lead', orgUnitId: nodeId };
    if (tier === 'executive') executive.push(participant);
    else if (tier === 'bottom') bottom.push(participant);
    else middle.push(participant);
  }

  const levelDeadlines = cadence
    ? cadence.stops
        .filter((s) => s.level > 0)
        .map((s) => ({ orgUnitId: s.nodeId, windowDays: s.windowDays, dueDate: s.dueDate }))
    : undefined;

  return {
    executive,
    middle,
    bottom,
    bottomRepresentedByLeads: true,
    defaultResponseDeadlineDays: params.deadlineDays,
    ...(levelDeadlines && levelDeadlines.length > 0 ? { levelDeadlines } : {}),
  };
}

/**
 * The stop the path's Track sits at. For a plain chain that is the last stop.
 * For a path that forks it is the stop where the fork happens — the last stop
 * every branch shares — because that is where the result is settled before it
 * goes to the several stops below.
 */
export function pathEndpointOrgUnitId(
  path: DesignerPath,
  orgById?: Map<string, Pick<OrgPathNode, 'parentId'>>,
): string | undefined {
  if (path.nodes.length === 0) return undefined;
  if (!orgById) return path.nodes[path.nodes.length - 1];
  let cursor = path.nodes[0];
  for (let guard = 0; guard < path.nodes.length; guard++) {
    const below = pathChildrenOf(path, cursor, orgById);
    if (below.length !== 1) break;
    cursor = below[0];
  }
  return cursor;
}
